import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Image, Text } from "@mantine/core";
import { AuthLayout } from 'src/components/Layout/AuthLayout';
import { VisualSideLayout } from 'src/components/Layout/VisualsLayout';
import { UserTypeToggle } from 'src/components/Toggles/UserTypeToggle';
import { LearnerSignInForm } from 'src/components/Forms/LearnerSignin';
import { ParentSignInForm } from 'src/components/Forms/ParentSignin';
import { authService } from "src/services/services/auth/auth.api";
import { ErrorKind, ResponseKind } from "src/services/services/MainService";
import { useAppDispatch } from 'src/redux/redux-store/hooks';
import { handleUserUpdate, updateAdminSettings, updateAppSettings, updateUser, handleWalkthroughUpdate } from 'src/redux/redux-store';
import { UserRole } from 'src/enums/User';
import { userRoutes } from 'src/navigators/routes/user';
import { useAuth } from 'src/hooks/useAuth';
import { authRoutes } from 'src/navigators/routes/auth';
import { AuthHeader } from 'src/components/Cards/AuthHeaderCard';
import { clearStorageToken } from 'src/utils/auth.storage';
import { parentRoutes } from 'src/navigators/routes/ParentRoutes';
import useResponsive from 'src/hooks/useResponsive';
import { FeedbackMessage } from 'src/components/common/FeedbackMessage/FeedbackMessage';
import { FormTransition } from 'src/components/common/FormTransition/FormTransition';
import { PageTransition } from 'src/components/common/PageTransition/PageTransition';
import { sendFailureNotification } from 'src/utils/notifications';
import { NotificationTypes } from 'src/enums/Notifications';
import { parentService } from 'src/services/services/parent/parent.api';

const SignIn: React.FC = () => {
  const [userType, setUserType] = useState<'Learner' | 'Parent'>('Learner');
  const dispatch = useAppDispatch()
  const [feedback, setFeedback] = useState({ message: '', title: '' })
  const url = window?.location?.href
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { login } = useAuth()
  const { isMobile } = useResponsive()

  useEffect(() => {
    if (url.includes("verified")) {
      setFeedback({ message: "Verification successful", title: "Account Verified" })
      setTimeout(() => setFeedback({ message: '', title: '' }), 3000)
    }
  }, [url])

  const navigateToSignUp = useCallback(() => {
    navigate(authRoutes.SIGNUP);
  }, []);

  const navigateToForgotPassword = useCallback(() => {
    navigate(authRoutes.FORGOT_PASSWORD)
  }, [])

  const loginAsChild = useCallback(async (values: any) => {
    setLoading(true);
    try {
      const response = await authService.logIn(
        values.username,
        values.password
      );
      console.log("response", response)
      if (response.kind === ResponseKind.OK) {
        const tokenData = response.data;

        if (tokenData) {
          if (tokenData.role === UserRole.user) {
            login(tokenData)
            navigate(userRoutes.DASHBOARD, { replace: true });
          } else {
            clearStorageToken()
            sendFailureNotification(NotificationTypes.Login, "Invalid credentials");
            return;
          }
        } else {
          sendFailureNotification(NotificationTypes.Login, "Invalid credentials");
        }
      } else if (response.kind === ErrorKind.AUTHORIZATION_ERROR) {
        sendFailureNotification(NotificationTypes.Login, (response as any).errors ?? "Invalid credentials");
        clearStorageToken();
      } else if (response.kind === ErrorKind.BAD_DATA_ERROR) {
        sendFailureNotification(NotificationTypes.Login, (response as any).errors ?? "Invalid credentials");
        clearStorageToken();
      } else {
        const errorMessage = 'errors' in response ? (response as any).errors : "Invalid credentials";
        sendFailureNotification(NotificationTypes.Login, errorMessage);
        clearStorageToken();
      }
    } catch (e) {
      console.error("Login error:", e);
      sendFailureNotification(NotificationTypes.Login, "An unexpected error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  }, []);


  const loginAsParent = useCallback(async (values: any) => {
    setLoading(true);
    try {
      const response = await authService.logIn(
        values.email,
        values.password
      );

      if (response.kind === ResponseKind.OK) {
        const tokenData = response.data;
        if (tokenData) {
          if (tokenData.role == UserRole.parent) {
            login(tokenData);
            const user = await dispatch(handleUserUpdate())

            if (user) {
              if (user.walkthrough_completed) {
                navigate('/parent', { replace: true })
              } else {
                // Check if the parent already has children (e.g. created by admin)
                try {
                  const childrenResponse = await parentService.getChildren();
                  if (childrenResponse.kind === ResponseKind.OK && childrenResponse.data && childrenResponse.data.children.length > 0) {
                    // Parent has children, skip onboarding
                    await dispatch(handleWalkthroughUpdate()) // Mark walkthrough as completed
                    navigate('/parent', { replace: true })
                  } else {
                    navigate(parentRoutes.PREPARE_LEARNERS, { replace: true })
                  }
                } catch (err) {
                  console.error("Error checking children on login", err);
                  navigate(parentRoutes.PREPARE_LEARNERS, { replace: true })
                }
              }
            } else {
              sendFailureNotification(NotificationTypes.Login, "Login failed: User not found");
              clearStorageToken();
            }
          } else {
            sendFailureNotification(NotificationTypes.Login, "Invalid credentials: Please login with a parent account");
            clearStorageToken();
          }
        } else {
          sendFailureNotification(NotificationTypes.Login, "Invalid credentials");
        }
      } else if (response.kind === ErrorKind.AUTHORIZATION_ERROR) {
        sendFailureNotification(NotificationTypes.Login, (response as any).errors ?? "Invalid credentials");
        clearStorageToken();
      }
      else if (response.kind === ErrorKind.BAD_DATA_ERROR) {
        sendFailureNotification(NotificationTypes.Login, (response as any).errors ?? "Invalid credentials");
        clearStorageToken();
      } else {
        const errorMessage = 'errors' in response ? (response as any).errors : "Invalid credentials";
        sendFailureNotification(NotificationTypes.Login, errorMessage);
        clearStorageToken();
      }
    } catch (error) {
      console.error("Login error:", error);
      sendFailureNotification(NotificationTypes.Login, 'An unexpected error occurred. Please try again.');
      clearStorageToken();
    } finally {
      setLoading(false);
    }
  }, []);

  const dismissFeedback = () => {
    setFeedback({ message: '', title: '' })
  }

  const formComponent = (
    <div className=" h-full flex flex-col justify-center space-y-6 w-full md:w-2/3">
      <AuthHeader title='Sign In' subtitle='Enter your details to sign in' />
      <FeedbackMessage feedback={feedback ? feedback.message : ""} title={feedback.title} handleClose={dismissFeedback} />
      <UserTypeToggle userType={userType} onToggle={setUserType} />
      <FormTransition key={userType}>
        {userType === 'Learner' ? (
          <LearnerSignInForm handleForgotPassword={navigateToForgotPassword} onSubmit={loginAsChild} loading={loading} error={error} />
        ) : (
          <ParentSignInForm handleForgotPassword={navigateToForgotPassword} onSubmit={loginAsParent} loading={loading} error={error} />
        )}
      </FormTransition>
      <Text className="text-center text-sm">
        Don't have an account? <a onClick={navigateToSignUp} className="text-blue-600 cursor-pointer">{userType == "Parent" ? 'Sign Up' : 'Get a parent to Sign up'}</a>
      </Text>
    </div>
  );

  return (
    <PageTransition key="signin">
      <AuthLayout
        visualComponent={<VisualSideLayout />}
        formComponent={formComponent}
      />
    </PageTransition>
  );
};

export default SignIn;


